import { ApiSentryLambdaFetch, awaitJSON } from '@src/utils/fetch-utils';
import {
  ApiSentryChange,
  ApiSentryRun,
  ApiSentrySource,
  ApiSentrySourceInput
} from '@blisspointmedia/bpm-types/dist/ApiSentry';

// The GET /changes/meta response shape (bpm-lambdas' apisentry/changes.ts
// getChangesMeta) - not in bpm-types since it's an API response composite, not a
// shared domain type.
interface ApiSentryChangesMeta {
  lastUpdated: string | null;
  totalChanges: number;
  latestRun: ApiSentryRun | null;
}

export const getChanges = async (filters?: {
  platformKey?: string;
  changeType?: string;
  effectiveFrom?: string;
  effectiveTo?: string;
}): Promise<ApiSentryChange[]> => {
  const res = await ApiSentryLambdaFetch('/changes', {
    params: filters
      ? Object.fromEntries(
          Object.entries(filters)
            .filter(([, v]) => v !== undefined)
            .map(([k, v]) => [k, String(v)])
        )
      : undefined
  });
  return awaitJSON<ApiSentryChange[]>(res);
};

export const getChangesMeta = async (): Promise<ApiSentryChangesMeta> => {
  const res = await ApiSentryLambdaFetch('/changes/meta');
  return awaitJSON<ApiSentryChangesMeta>(res);
};

export const listAdminSources = async (): Promise<ApiSentrySource[]> => {
  const res = await ApiSentryLambdaFetch('/admin/sources');
  return awaitJSON<ApiSentrySource[]>(res);
};

export const createAdminSource = async (input: ApiSentrySourceInput): Promise<ApiSentrySource> => {
  const res = await ApiSentryLambdaFetch('/admin/sources', {
    method: 'POST',
    body: input
  });
  return awaitJSON<ApiSentrySource>(res);
};

export const updateAdminSource = async (
  id: string,
  input: ApiSentrySourceInput
): Promise<ApiSentrySource> => {
  const res = await ApiSentryLambdaFetch(`/admin/sources/${id}`, {
    method: 'PUT',
    body: input
  });
  return awaitJSON<ApiSentrySource>(res);
};

export const deleteAdminSource = async (id: string): Promise<void> => {
  await ApiSentryLambdaFetch(`/admin/sources/${id}`, { method: 'DELETE' });
};

export const setAdminSourceEnabled = async (id: string, enabled: boolean): Promise<void> => {
  await ApiSentryLambdaFetch(`/admin/sources/${id}/enabled`, {
    method: 'POST',
    body: { enabled }
  });
};

export const startRun = async (input?: {
  platforms?: string[];
  baselineDate?: string;
}): Promise<{ runId: string }> => {
  const res = await ApiSentryLambdaFetch('/admin/runs', {
    method: 'POST',
    body: input ?? {}
  });
  return awaitJSON<{ runId: string }>(res);
};

export const getRunStatus = async (runId: string): Promise<ApiSentryRun> => {
  const res = await ApiSentryLambdaFetch(`/admin/runs/${runId}`);
  return awaitJSON<ApiSentryRun>(res);
};
